import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import {
  Alert,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material"
import AppApi from "../utils/AppApi"
import { ROLE_OPTIONS } from "../utils/roles"

const emptyForm = {
  username: "",
  fullname: "",
  password: "",
  role: "Guest",
  permission: "",
  disabled: false,
}

const errorMessage = (err) =>
  err.response?.data?.message ||
  err.response?.data?.error ||
  err.message ||
  "Something went wrong"

const UserManagement = () => {
  const me = useSelector((state) => state.auth.user)
  const [users, setUsers] = useState([])
  const [error, setError] = useState(null)
  const [dialog, setDialog] = useState(null) // { mode: "create"|"edit"|"password"|"delete", user }
  const [form, setForm] = useState(emptyForm)
  const [dialogError, setDialogError] = useState(null)
  const [saving, setSaving] = useState(false)

  const loadUsers = async () => {
    try {
      const result = await AppApi.get("/admin/users")
      setUsers(result.data)
      setError(null)
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  useEffect(() => {
    loadUsers()
  }, [])

  const openDialog = (mode, user = null) => {
    setDialogError(null)
    setForm(
      user
        ? {
            ...emptyForm,
            username: user.username,
            fullname: user.fullname,
            role: user.roles[0] || "Guest",
            permission: user.permission.join(", "),
            disabled: user.disabled,
          }
        : emptyForm,
    )
    setDialog({ mode, user })
  }

  const closeDialog = () => setDialog(null)

  const parsePermission = (text) =>
    text
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean)

  const handleSave = async () => {
    setSaving(true)
    setDialogError(null)
    try {
      const { mode, user } = dialog
      if (mode === "create") {
        await AppApi.post("/admin/users", {
          username: form.username,
          fullname: form.fullname,
          password: form.password,
          roles: [form.role],
          permission: parsePermission(form.permission),
        })
      } else if (mode === "edit") {
        await AppApi.patch(`/admin/users/${encodeURIComponent(user.username)}`, {
          fullname: form.fullname,
          roles: [form.role],
          permission: parsePermission(form.permission),
          disabled: form.disabled,
        })
      } else if (mode === "password") {
        await AppApi.patch(
          `/admin/users/${encodeURIComponent(user.username)}/password`,
          { password: form.password },
        )
      } else if (mode === "delete") {
        await AppApi.delete(`/admin/users/${encodeURIComponent(user.username)}`)
      }
      closeDialog()
      loadUsers()
    } catch (err) {
      setDialogError(errorMessage(err))
    }
    setSaving(false)
  }

  const isSelf = dialog?.user?.username === me.username
  const titles = {
    create: "Create User",
    edit: `Edit ${dialog?.user?.username}`,
    password: `Reset Password: ${dialog?.user?.username}`,
    delete: `Delete ${dialog?.user?.username}?`,
  }

  return (
    <Container maxWidth="xl" style={{ paddingBottom: "20px" }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ my: 2 }}
      >
        <h1>User Management</h1>
        <Button variant="contained" onClick={() => openDialog("create")}>
          Add User
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Full name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Customer permission</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.username}>
                <TableCell>
                  <b>{u.username}</b>
                </TableCell>
                <TableCell>{u.fullname}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={u.roles.join(", ") || "-"}
                    color={
                      u.roles.includes("SuperAdmin")
                        ? "error"
                        : u.roles.includes("Admin")
                          ? "primary"
                          : "default"
                    }
                  />
                </TableCell>
                <TableCell>
                  {u.permission.length > 0 ? u.permission.join(", ") : "-"}
                </TableCell>
                <TableCell>
                  {u.disabled ? (
                    <Chip size="small" label="Disabled" />
                  ) : (
                    <Chip size="small" label="Active" color="success" />
                  )}
                </TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => openDialog("edit", u)}>
                    Edit
                  </Button>
                  <Button
                    size="small"
                    onClick={() => openDialog("password", u)}
                  >
                    Reset Password
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    disabled={u.username === me.username}
                    onClick={() => openDialog("delete", u)}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={dialog !== null}
        onClose={closeDialog}
        maxWidth="xs"
        fullWidth
      >
        {dialog && (
          <>
            <DialogTitle>{titles[dialog.mode]}</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                {dialogError && <Alert severity="error">{dialogError}</Alert>}

                {dialog.mode === "delete" && (
                  <div>
                    ลบผู้ใช้ <b>{dialog.user.username}</b> ถาวร
                    ถ้าต้องการเพียงระงับการใช้งานให้ใช้ Edit → Disabled แทน
                  </div>
                )}

                {dialog.mode === "create" && (
                  <TextField
                    label="Username"
                    value={form.username}
                    onChange={(e) =>
                      setForm({ ...form, username: e.target.value })
                    }
                  />
                )}

                {(dialog.mode === "create" || dialog.mode === "edit") && (
                  <>
                    <TextField
                      label="Full name"
                      value={form.fullname}
                      onChange={(e) =>
                        setForm({ ...form, fullname: e.target.value })
                      }
                    />
                    <TextField
                      select
                      label="Role"
                      value={form.role}
                      disabled={isSelf}
                      onChange={(e) =>
                        setForm({ ...form, role: e.target.value })
                      }
                    >
                      {ROLE_OPTIONS.map((r) => (
                        <MenuItem key={r} value={r}>
                          {r}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label="Customer permission"
                      helperText="รหัสลูกค้าคั่นด้วย , เช่น SSI, TCR (Guest ที่ว่างจะไม่เห็น DN)"
                      value={form.permission}
                      onChange={(e) =>
                        setForm({ ...form, permission: e.target.value })
                      }
                    />
                  </>
                )}

                {dialog.mode === "edit" && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.disabled}
                        disabled={isSelf}
                        onChange={() =>
                          setForm({ ...form, disabled: !form.disabled })
                        }
                      />
                    }
                    label="Disabled"
                  />
                )}

                {(dialog.mode === "create" || dialog.mode === "password") && (
                  <TextField
                    label={dialog.mode === "create" ? "Password" : "New password"}
                    type="password"
                    helperText="อย่างน้อย 6 ตัวอักษร"
                    value={form.password}
                    onChange={(e) =>
                      setForm({ ...form, password: e.target.value })
                    }
                  />
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog}>Cancel</Button>
              <Button
                variant="contained"
                color={dialog.mode === "delete" ? "error" : "primary"}
                onClick={handleSave}
                disabled={saving}
              >
                {dialog.mode === "delete" ? "Delete" : "Save"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  )
}

export default UserManagement
